import React from 'react';
import { makeStyles } from '@mui/styles';
import { AppBar, Menu, MenuItem, Link, Grid } from '@mui/material';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import StopCircle from '@mui/icons-material/StopCircle';
import Typography from '@mui/material/Typography';
import AccountCircle from '@mui/icons-material/AccountCircle';
import Snackbar from '@mui/material/Snackbar';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { BrowserUtils } from '@azure/msal-browser';
import { AuthenticationContext } from './auth/contexts/AuthenticationContext';
import ImpersonateUserModal from './auth/components/ImpersonateUserModal';
import { useLocation as useRouterLocation } from 'react-router-dom';
import ChillerLocationToggle from '../chillerModules/navigation/components/ChillerLocationToggle';
import CollectionsLocationToggle from '../collectionsModules/navigation/components/CollectionsLocationToggle';
import { useCollectionsPermissions } from '../collectionsModules/shared/useCollectionsPermissions';
import WarehouseLocationToggle from '../warehouseModules/navigation/components/WarehouseLocationToggle';

const useStyles = makeStyles((theme: any) => ({
  root: {
    boxShadow: '0 1px 6px 0 rgb(32 33 36 / 28%)',
  },
  grow: {
    flexGrow: 1,
  },
  toolbar: {
    backgroundColor: '#fff',
  },
  menuButton: {
    marginRight: 2,
  },
  inputRoot: {
    color: 'inherit',
    border: '1px solid #ccc',
  },
  sectionDesktop: {
    display: 'none',
    flexGrow: 1,

    '@media (min-width: 960px)': {
      display: 'flex',
    },
  },
  sectionMobile: {
    display: 'flex',
    flexGrow: 1,

    '@media (min-width: 960px)': {
      display: 'none',
    },
  },
  logoContainer: {
    '@media (max-width: 768px)': {
      display: 'none',
    },
  },
  mobileLogoContainer: {
    display: 'none',
    '@media (max-width: 768px)': {
      display: 'flex',
      alignItems: 'center',
      marginLeft: 8,
    },
  },
}));

interface UserTopNavigationprops {
  // Omitted on pages without a sidebar, which hides the hamburger (nothing to open or collapse).
  onNavigationToggle?: () => void;
}

const UserTopNavigation: React.FunctionComponent<UserTopNavigationprops> = (props) => {
  const { onNavigationToggle } = props;
  const classes = useStyles();
  const { logout, user } = React.useContext(AuthenticationContext);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);
  const [browserCacheNotification, setBrowserCacheNotification] = React.useState(false);
  const [impersonateModalOpen, setImpersonateModalOpen] = React.useState(false);

  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const userAccount = accounts[0];

  React.useEffect(() => {
    if (userAccount?.username) {
      sessionStorage.setItem('microsoftAuthId', userAccount.homeAccountId);
    }
  }, [userAccount]);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClearBrowserCache = () => {};

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenImpersonateModal = () => {
    setImpersonateModalOpen(true);
    handleMenuClose();
  };

  const menuId = 'primary-search-account-menu';

  const handleLogOut = async () => {
    // logout() navigates to /login immediately, which can cut off logoutRedirect before it clears the
    // cached MSAL account.  Clear local state here and let logoutRedirect do the navigation.
    localStorage.removeItem('token');
    sessionStorage.removeItem('redirectAfterLogin');

    try {
      await instance.logoutRedirect({
        account: instance.getActiveAccount(),
        postLogoutRedirectUri: 'https://sales.tudi.com/login',
      });
    } catch (error) {
      // MSAL could not start the logout; fall back to clearing the app session so the user isn't left signed in.
      console.error(error);
      logout();
    }
  };

  const isAdminUser = user?.modules?.some((module) => module.value === 'admin' && parseInt(module.tier) > 2);
  const { isCollectionsAdmin } = useCollectionsPermissions();
  const routerLocation = useRouterLocation();
  const isChillerModule = routerLocation.pathname.includes('/chiller_logs');
  const isCollectionsModule = routerLocation.pathname.includes('/collections');
  // Not gated on isAdminUser: receivers are ordinary users, and the location toggle is the only
  // thing that makes a PO lookup (and warehouse favorites) meaningful for them.
  const isWarehouseModule = routerLocation.pathname.includes('/warehouse');

  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      id={menuId}
      keepMounted
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      {isAdminUser && <MenuItem onClick={handleOpenImpersonateModal}>Impersonate User</MenuItem>}
      <MenuItem onClick={handleLogOut}>Logout</MenuItem>
    </Menu>
  );

  const handleStopImpersonationClicked = () => {
    sessionStorage.setItem('impersonatedAuthId', null);
    window.location.reload();
  };

  return (
    <div className={classes.grow}>
      <ImpersonateUserModal open={impersonateModalOpen} onClose={() => setImpersonateModalOpen(false)} />
      <Snackbar
        open={browserCacheNotification}
        autoHideDuration={6000}
        onClose={() => setBrowserCacheNotification(false)}
        message="Your browser cache has been successfully cleared."
      />
      <AppBar position="fixed" className={classes.root}>
        <Toolbar className={classes.toolbar}>
          <Grid container justifyContent="space-between">
            {/* Row so the hamburger sits beside the logo; flex-end lines it up with the desktop logo's 15px top offset. */}
            <Grid item style={{ display: 'flex', alignItems: 'flex-end' }}>
              {onNavigationToggle && (
                <button
                  type="button"
                  className="Polaris-TopBar__NavigationIcon"
                  aria-label="Toggle menu"
                  onClick={onNavigationToggle}
                >
                  <span className="Polaris-Icon">
                    <svg viewBox="0 0 20 20" className="Polaris-Icon__Svg" focusable="false" aria-hidden="true">
                      <path d="M19 11H1a1 1 0 0 1 0-2h18a1 1 0 1 1 0 2zm0-7H1a1 1 0 0 1 0-2h18a1 1 0 1 1 0 2zm0 14H1a1 1 0 0 1 0-2h18a1 1 0 0 1 0 2z" />
                    </svg>
                  </span>
                </button>
              )}
              <Grid container>
                <Grid item className={classes.logoContainer}>
                  <a href={'/home'}>
                    <img
                      alt="logo"
                      src={
                        'https://www.tudi.com/wp-content/themes/boxpress/assets/img/dist/branding/tudi-mechanical-logo@2x.png'
                      }
                      style={{ height: '40px', marginTop: 15 }}
                    />
                  </a>
                </Grid>
                <Grid item className={classes.mobileLogoContainer}>
                  <a href={'/home'}>
                    <img
                      alt="logo"
                      src={
                        'https://www.tudi.com/wp-content/themes/boxpress/assets/img/dist/branding/tudi-mechanical-logo@2x.png'
                      }
                      style={{ height: '28px', display: 'block' }}
                    />
                  </a>
                </Grid>
              </Grid>
            </Grid>
            {isChillerModule && isAdminUser && (
              <Grid item style={{ display: 'flex', alignItems: 'center', marginLeft: 16 }}>
                <ChillerLocationToggle />
              </Grid>
            )}
            {isCollectionsModule && isCollectionsAdmin && (
              <Grid item style={{ display: 'flex', alignItems: 'center', marginLeft: 16 }}>
                <CollectionsLocationToggle />
              </Grid>
            )}
            {isWarehouseModule && (
              <Grid item style={{ display: 'flex', alignItems: 'center', marginLeft: 16 }}>
                <WarehouseLocationToggle />
              </Grid>
            )}
            {user?.current_user && (
              <Grid item style={{ backgroundColor: 'indianred', paddingLeft: 25, paddingRight: 25 }}>
                <div className={classes.grow} />
                <div className={classes.sectionDesktop}>
                  <div style={{}}>
                    <Typography style={{ lineHeight: '65px', color: 'black', display: 'flex', alignItems: 'center' }}>
                      Impersonating: {user.last_name_first} &emsp;{' '}
                      <StopCircle
                        style={{ color: 'white', cursor: 'pointer' }}
                        onClick={handleStopImpersonationClicked}
                      />
                    </Typography>
                  </div>
                </div>
              </Grid>
            )}
            <Grid item>
              <div className={classes.grow} />
              <div className={classes.sectionDesktop}>
                <div>
                  <Typography style={{ lineHeight: '65px', color: 'black' }}>
                    {userAccount?.username} ({isAuthenticated ? 'Azure AD' : 'Internal'})
                  </Typography>
                </div>
                <IconButton edge="end" color="primary" onClick={handleProfileMenuOpen}>
                  <AccountCircle style={{ fontSize: '2.2rem' }} />
                </IconButton>
              </div>
              <div className={classes.sectionMobile}>
                <IconButton edge="end" color="primary" onClick={handleProfileMenuOpen}>
                  <AccountCircle style={{ fontSize: '2.2rem' }} />
                </IconButton>
              </div>
            </Grid>
          </Grid>
          {renderMenu}
        </Toolbar>
      </AppBar>
    </div>
  );
};

export default UserTopNavigation;
